import React, { useState, useEffect, useMemo } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { viVN } from '@mui/x-date-pickers/locales';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { IconButton, InputAdornment } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { formatToDate, formatToSeconds } from 'src/utils/date-locale';

const DateAndTimePicker = forwardRef((props, ref) => {
  const {
    name,
    format = 'dd/MM/yyyy',
    hintValue,
    defaultValue,
    ignoreDefault = false,
    formik,
    onDateChanged,
    children,
    ...otherProps
  } = props;

  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [isHintActive, setHintActive] = useState(false); // Use hint color only if no selected value
  const [isClearActive, setClearActive] = useState(false);
  const [actualValue , setActualValue ] = useState(null);

  const currentDate = useMemo(() => {
    // 1970-01-01 8:00:01 = 1
    if (ignoreDefault) return null;
    if (hintValue) return null;
    if (defaultValue) return formatToDate(defaultValue);
    return Date.now();
  }, [hintValue, defaultValue]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      const timestamp = hintValue ? formatToDate(hintValue) : currentDate;
      setActualValue(timestamp);
      setHintActive(Boolean(hintValue));
      setClearActive(false);
      if (formik) {
        formik.setFieldValue(name, formatToSeconds(currentDate));
      }
    }
  }));

  useEffect(() => {
    const timestamp = hintValue ? formatToDate(hintValue) : currentDate;
    setActualValue(timestamp);

    setHintActive(Boolean(hintValue));
    if(formik) formik.setFieldValue(name, formatToSeconds(currentDate));
  }, []);

  useEffect(() => {
    if (hintValue) {
      const parsed = formatToDate(hintValue);
      setActualValue(parsed);
      setHintActive(true);
      setClearActive(false);
    }else{
      setActualValue(currentDate);
      setHintActive(false);
      setClearActive(false);
    }
  }, [hintValue, currentDate]);
  
  const handleDateChange = (date) => {
    // Trigger the callback if provided
    if (typeof onDateChanged === 'function') {
      const seconds = formatToSeconds(date);
      onDateChanged(seconds);
    }
    setActualValue(date);

    const timestamp = hintValue ? formatToDate(hintValue) : currentDate;
    const isTemp = formatToSeconds(timestamp) !== formatToSeconds(date);
    setHintActive(Boolean(!timestamp || !isTemp));
    setClearActive(isTemp);
    
    // Check if formik is null or undefined
    if (!formik) return; 
    const newFilters = {
      ...formik.values.filters,
      [name]: date || undefined,
    };

    if (
      name === 'startDate' &&
      newFilters.endDate &&
      date &&
      date > newFilters.endDate
    ) {
      newFilters.endDate = date;
    }
    formik.setFieldValue('filters', newFilters);
    formik.setFieldValue(name, formatToSeconds(date));

    if(date){
      formik.setFieldError(name, undefined);
    }
  };

  const handleClear = (event) => {
    event.stopPropagation();
    // Trigger the callback if provided with null value
    if (typeof onDateChanged === 'function') {
      onDateChanged(0);
    }
    const timestamp = hintValue ? formatToDate(hintValue) : currentDate;
    setActualValue(timestamp);

    setHintActive(Boolean(hintValue));
    setClearActive(false);

    // Check if formik is null or undefined
    if (!formik) return;
    const newFilters = {
      ...formik.values.filters,
      [name]: currentDate ?? undefined,
    };

    // Reset show value is default as hintValue or defaultValue
    formik.setFieldValue('filters', newFilters);
    formik.setFieldValue(name, formatToSeconds(currentDate));
  };

  const hasError =
    formik &&
    formik.touched[name] &&
    Boolean(formik.errors[name]);

  const errorText = hasError ? formik.errors[name] : undefined;

  const ChildComponent =
    React.Children.count(children) > 0 ? children : <MobileDatePicker />;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      localeText={
        viVN.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      {React.cloneElement(ChildComponent, {
        value: actualValue,
        onChange: handleDateChange,
        format,
        slotProps: {
          textField: {
            fullWidth: true,
            variant: 'outlined',
            placeholder: hintValue ? formatToDate(hintValue, format) : '',
            error: hasError,
            helperText: errorText,
            InputLabelProps: {
              // shrink: Boolean(hintValue) || Boolean(currentDate)
              shrink: Boolean(actualValue)
            },
            InputProps: {
              sx: {
                color: isHintActive ? theme.palette.text.hint : theme.palette.text.primary,
                '&::placeholder': { color: theme.palette.text.hint },
              },
              endAdornment: (
                <InputAdornment position="end">
                  {isClearActive && (
                    <IconButton onClick={handleClear} edge="end">
                      <ClearIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          },
          dialog: {
            closeAfterTransition: false
          }
        },
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        ...otherProps,
      })}
    </LocalizationProvider>
  );
});

DateAndTimePicker.propTypes = {
  formik: PropTypes.object,
  hintValue: PropTypes.number,
  defaultValue: PropTypes.number,
  onDateChanged: PropTypes.func, // Callback for date change
};

export default DateAndTimePicker;
